// components/finalClient/chatbot/ProductChat.jsx
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import axios from 'axios';
import { Send, ShoppingBag, Loader, CheckCircle, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import { socket } from '../../admin/chat/socket';

const API_URL = process.env.REACT_APP_API_URL;

function ProductChat() {
  const { sku, sellerId } = useParams();
  const [product, setProduct] = useState(null);
  const [seller, setSeller] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [orderSaved, setOrderSaved] = useState(false);
  const [buyerDetails, setBuyerDetails] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageTransitioning, setImageTransitioning] = useState(false);
  const messagesEndRef = useRef(null);
  const isInitialized = useRef(false);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize chat on component mount
  useEffect(() => {
    if (isInitialized.current) {
      console.log('[ProductChat] Already initialized, skipping...');
      return;
    }

    async function initializeChat() {
      try {
        setIsLoading(true);
        
        console.log('[ProductChat] Starting initialization...');
        
        // Get product and seller info
        const productResponse = await axios.get(
          `${API_URL}/api/chat/product/${sku}/seller/${sellerId}`
        );
        
        setProduct(productResponse.data.product);
        setSeller(productResponse.data.seller);
        
        // Start chat session
        const sessionResponse = await axios.post(
          `${API_URL}/api/chat/session/start`,
          { seller_id: parseInt(sellerId) }
        );
        
        const newSessionId = sessionResponse.data.session_id;
        setSessionId(newSessionId);
        
        console.log(`[ProductChat] Session created: ${newSessionId}`);
        
        // Send initial buyer message
        const initialMessage = `Hola, estoy interesado en ${productResponse.data.product.title}`;
        
        const messageResponse = await axios.post(
          `${API_URL}/api/chat/message`,
          {
            session_id: newSessionId,
            message: initialMessage,
            sku: sku,
            seller_id: parseInt(sellerId)
          }
        );
        
        // Add both messages to chat
        setMessages([
          {
            id: 1,
            sender: 'buyer',
            content: initialMessage,
            timestamp: new Date().toISOString()
          },
          {
            id: 2,
            sender: 'bot',
            content: messageResponse.data.message,
            timestamp: messageResponse.data.timestamp
          }
        ]);
        
        // Join Socket.IO room for this chat session
        const room = `chat_${newSessionId}`;
        socket.emit('admin_join_chat', room);
        console.log(`[ProductChat] Joined Socket.IO room: ${room}`);
        
        isInitialized.current = true;
        
      } catch (error) {
        console.error('Error initializing chat:', error);
      } finally {
        setIsLoading(false);
      }
    }

    if (sku && sellerId) {
      initializeChat();
    }

    return () => {
      console.log('[ProductChat] Component unmounting...');
    };
  }, [sku, sellerId]);

  // Set up Socket.IO listeners for real-time messages
  useEffect(() => {
    if (!sessionId) return;

    const handleNewMessage = (data) => {
      console.log('[ProductChat] New message received via Socket.IO:', data);
      
      if (data.session_id === sessionId) {
        const newMessage = data.message;
        
        if (newMessage.sender === 'admin') {
          setMessages(prev => {
            const exists = prev.some(msg => msg.id === newMessage.id);
            if (exists) return prev;
            
            return [...prev, newMessage];
          });
        }
      }
    };

    socket.on('new_message', handleNewMessage);

    return () => {
      socket.off('new_message', handleNewMessage);
      if (sessionId) {
        const room = `chat_${sessionId}`;
        socket.emit('admin_leave_chat', room);
        console.log(`[ProductChat] Left Socket.IO room: ${room}`);
      }
    };
  }, [sessionId]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || !sessionId) return;
    
    const messageText = inputMessage.trim();
    setInputMessage('');
    setIsSending(true);
    
    const userMessage = {
      id: Date.now(),
      sender: 'buyer',
      content: messageText,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/chat/message`,
        {
          session_id: sessionId,
          message: messageText,
          sku: sku,
          seller_id: parseInt(sellerId)
        }
      );
      
      if (response.data.order_saved) {
        setOrderSaved(true);
        setBuyerDetails(response.data.buyer_details);
      }
      
      const botMessage = {
        id: Date.now() + 1,
        sender: 'bot',
        content: response.data.message,
        timestamp: response.data.timestamp,
        isConfirmation: response.data.order_saved
      };
      
      setMessages(prev => [...prev, botMessage]);
      
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        sender: 'bot',
        content: 'Lo siento, hubo un error. Por favor intenta de nuevo.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  // Image gallery functions
  const handlePrevImage = () => {
    if (!product?.images || product.images.length <= 1) return;
    setImageTransitioning(true);
    setTimeout(() => {
      setCurrentImageIndex((prev) => 
        prev === 0 ? product.images.length - 1 : prev - 1
      );
      setImageTransitioning(false);
    }, 150);
  };

  const handleNextImage = () => {
    if (!product?.images || product.images.length <= 1) return;
    setImageTransitioning(true);
    setTimeout(() => {
      setCurrentImageIndex((prev) => 
        prev === product.images.length - 1 ? 0 : prev + 1
      );
      setImageTransitioning(false);
    }, 150);
  };

  // WhatsApp function
  const handleWhatsAppClick = () => {
    const phone = '+51977802104';
    const message = `Hola, estoy interesado en ${product?.title || 'este producto'}`;
    const whatsappUrl = `https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-brand-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Cargando chat...</p>
        </div>
      </div>
    );
  }

  if (!product || !seller) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-600">Producto no encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-brand-primary text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
              <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-montserrat font-bold text-base sm:text-lg truncate">
                {product.title}
              </h1>
              <p className="text-xs sm:text-sm opacity-90">{seller.username}</p>
            </div>
            
            {/* WhatsApp Button - Official Icon */}
            <button
              onClick={handleWhatsAppClick}
              className="bg-[#25D366] hover:bg-[#20BA5A] text-white p-2.5 sm:p-3 rounded-full shadow-lg transform transition-all hover:scale-110 active:scale-95 flex-shrink-0 animate-pulse"
              title="Contactar por WhatsApp"
            >
              <FaWhatsapp className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            
            <div className="text-right flex-shrink-0">
              <p className="text-lg sm:text-2xl font-bold">S/. {product.price}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Product Image Gallery - Always Visible, Responsive */}
      {product.images && product.images.length > 0 && (
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 py-2 sm:py-3 relative">
            {/* Image with fade transition */}
            <img
              src={product.images[currentImageIndex]?.url}
              alt={product.images[currentImageIndex]?.alt_text || product.title}
              className={`h-24 sm:h-32 w-auto mx-auto object-contain rounded-lg transition-opacity duration-300 ${
                imageTransitioning ? 'opacity-50' : 'opacity-100'
              }`}
            />
            
            {/* Navigation Arrows */}
            {product.images.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  disabled={imageTransitioning}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-1.5 sm:p-2 rounded-full shadow-lg transition-all hover:scale-110 disabled:opacity-50"
                  aria-label="Imagen anterior"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                
                <button
                  onClick={handleNextImage}
                  disabled={imageTransitioning}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-1.5 sm:p-2 rounded-full shadow-lg transition-all hover:scale-110 disabled:opacity-50"
                  aria-label="Imagen siguiente"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                
                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-xs sm:text-sm">
                  {currentImageIndex + 1} / {product.images.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Order Confirmation Banner */}
      {orderSaved && (
        <div className="bg-brand-green text-white">
          <div className="max-w-4xl mx-auto px-4 py-2 sm:py-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="text-sm sm:text-base font-semibold">¡Pedido confirmado!</span>
            </div>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4 space-y-3 sm:space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender === 'buyer' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] sm:max-w-[80%] md:max-w-[70%] rounded-2xl px-3 sm:px-4 py-2 sm:py-3 ${
                  msg.sender === 'buyer'
                    ? 'bg-brand-primary text-white rounded-br-none'
                    : msg.sender === 'admin'
                    ? 'bg-purple-500 text-white rounded-bl-none shadow-sm'
                    : msg.isConfirmation
                    ? 'bg-brand-green text-white shadow-lg rounded-bl-none'
                    : 'bg-white text-gray-800 shadow-sm rounded-bl-none'
                }`}
              >
                {msg.sender === 'admin' && (
                  <p className="text-xs font-semibold mb-1 opacity-90">
                    👤 Administrador
                  </p>
                )}
                
                <p className="text-sm sm:text-base whitespace-pre-wrap break-words">
                  {msg.content}
                </p>
                <p
                  className={`text-xs mt-1 ${
                    msg.sender === 'buyer' 
                      ? 'text-white/70' 
                      : msg.sender === 'admin'
                      ? 'text-white/70'
                      : msg.isConfirmation 
                      ? 'text-white/70' 
                      : 'text-gray-500'
                  }`}
                >
                  {new Date(msg.timestamp).toLocaleTimeString('es-PE', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t shadow-lg">
        <div className="max-w-4xl mx-auto p-3 sm:p-4">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={orderSaved ? "Pedido confirmado ✓" : "Escribe un mensaje..."}
              disabled={isSending || orderSaved}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <button
              type="submit"
              disabled={isSending || !inputMessage.trim() || orderSaved}
              className="bg-brand-primary text-white p-2 sm:p-3 rounded-full hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex-shrink-0"
            >
              {isSending ? (
                <Loader className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" />
              ) : orderSaved ? (
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
              ) : (
                <Send className="w-5 h-5 sm:w-6 sm:h-6" />
              )}
            </button>
          </form>
          
          {/* Powered by */}
          <div className="text-center mt-2 sm:mt-3">
            <p className="text-xs text-gray-500">
              Powered by <span className="font-montserrat font-bold text-brand-primary">FAST MAX</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductChat;